/**
 * Audit event metadata — single source of truth for the visual styling
 * of every event_type produced by the action lifecycle.
 *
 * Used by:
 *   - pages/AuditLog.jsx           (the global feed at /audit)
 *   - components/shared/ActionHistoryTimeline.jsx (per-action timeline
 *     embedded in detail dialogs)
 *
 * Keep this in lockstep with docs/db-schema/README.md — every event_type
 * the backend can emit has an entry here.
 */
import {
  AlertCircle, Activity, X as XIcon, RotateCcw,
  CheckCircle2, XCircle, MessageSquare, DollarSign,
} from 'lucide-react'

export const EVENT_META = {
  // ── engine (action_calculations) ──────────────────────────────────────
  detected:         { label: 'Detected',     icon: AlertCircle,    color: 'text-cyan-300',    bg: 'bg-cyan-500/15',    border: 'border-cyan-500/30',    source: 'engine' },
  changed:          { label: 'Changed',      icon: Activity,       color: 'text-amber-300',   bg: 'bg-amber-500/15',   border: 'border-amber-500/30',   source: 'engine' },
  closed:           { label: 'Closed',       icon: XIcon,          color: 'text-slate-400',   bg: 'bg-white/5',         border: 'border-white/10',        source: 'engine' },
  reopened:         { label: 'Reopened',     icon: RotateCcw,      color: 'text-purple-300',  bg: 'bg-purple-500/15',  border: 'border-purple-500/30',  source: 'engine' },
  expired:          { label: 'Expired',      icon: XIcon,          color: 'text-slate-500',   bg: 'bg-white/5',         border: 'border-white/10',        source: 'engine' },
  // ── user (actions_acknowledge) ────────────────────────────────────────
  marked_done:      { label: 'Accepted',     icon: CheckCircle2,   color: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', source: 'user' },
  marked_dismissed: { label: 'Dismissed',    icon: XCircle,        color: 'text-slate-300',   bg: 'bg-white/5',         border: 'border-white/10',        source: 'user' },
  marked_undone:    { label: 'Reopened',     icon: RotateCcw,      color: 'text-amber-300',   bg: 'bg-amber-500/15',   border: 'border-amber-500/30',   source: 'user' },
  comment_added:    { label: 'Comment',      icon: MessageSquare,  color: 'text-sky-300',     bg: 'bg-sky-500/15',     border: 'border-sky-500/30',     source: 'user' },
  value_set:        { label: 'Actual P&L',   icon: DollarSign,     color: 'text-violet-300',  bg: 'bg-violet-500/15',  border: 'border-violet-500/30',  source: 'user' },
}

export const ENGINE_EVENT_TYPES = Object.entries(EVENT_META)
  .filter(([, m]) => m.source === 'engine')
  .map(([k]) => k)

export const USER_EVENT_TYPES = Object.entries(EVENT_META)
  .filter(([, m]) => m.source === 'user')
  .map(([k]) => k)

export const NODE_TYPES = ['Store', 'DC', 'Lane', 'Supplier']

export const CATEGORIES = [
  'LABOR_FLEX', 'LABOR_SURGE_PREP', 'ALLOCATION_HOLD',
  'REALLOCATION_OPPORTUNITY', 'PREDEPLOYMENT', 'DC_YARD_CLEARANCE',
  'INBOUND_RISK', 'LANE_RISK', 'SURGE_WARNING',
]
